import { memo } from 'react';

import { BaseTag } from '@/components/chariot-ui';
import { RiskSeverity } from '@/types';
import { cn } from '@/utils/classname';

export interface SeverityIndicatorProps {
  readonly severity: RiskSeverity;
  readonly count: number;
  readonly size?: 'xs' | 'sm' | 'md' | 'lg';
  readonly showZero?: boolean;
  readonly animate?: boolean;
  readonly className?: string;
  readonly 'data-testid'?: string;
  readonly onClick?: (severity: RiskSeverity, count: number) => void;
}

// Format number more efficiently
const formatCount = (count: number): string => {
  if (count === 0) return '0';
  if (count >= 10000) return `${Math.floor(count / 1000)}k`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}k`;
  return count.toString();
};

export const SeverityIndicator = memo<SeverityIndicatorProps>(
  function SeverityIndicator({
    severity,
    count,
    size = 'sm',
    showZero = false,
    animate = true,
    className,
    'data-testid': testId,
    onClick,
  }) {
    const isEmpty = count === 0 || count == null;

    // Don't render if empty and showZero is false
    if (isEmpty && !showZero) {
      return null;
    }

    // Move computation outside render
    const displayValue = formatCount(count);
    const isHighCount = count >= 100;
    const isCriticalOrHigh =
      severity === RiskSeverity.Critical || severity === RiskSeverity.High;
    const shouldPulse = isHighCount && isCriticalOrHigh && animate && !isEmpty;

    const severityClasses = {
      [RiskSeverity.Critical]:
        'bg-chariot-status-critical border-chariot-stroke-critical text-white',
      [RiskSeverity.High]:
        'bg-chariot-status-high border-chariot-stroke-high text-white',
      [RiskSeverity.Medium]:
        'bg-chariot-status-medium-strong border-chariot-stroke-medium text-white',
      [RiskSeverity.Low]:
        'bg-chariot-status-low border-chariot-stroke-low text-white',
      [RiskSeverity.Info]:
        'bg-chariot-status-info border-chariot-stroke-info text-white',
      [RiskSeverity.Exposure]:
        'bg-chariot-status-info border-chariot-stroke-info text-white',
    };

    return (
      <BaseTag
        className={cn(
          'inline-flex items-center justify-center',
          'flex min-w-9 items-center px-2.5 py-1',
          'transition-all duration-200 ease-in-out',
          severityClasses[severity],
          displayValue === '0' &&
            'bg-chariot-none border-chariot-status-low text-chariot-status-low',
          className,
          shouldPulse && 'animate-pulse font-bold',
          animate && 'transform hover:scale-110 hover:shadow-md',
          onClick ? 'cursor-pointer' : ''
        )}
        size={size}
      >
        <span data-testid={testId} className="uppercase">
          {displayValue}
        </span>
      </BaseTag>
    );
  }
);
